import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';

import Grid from '@material-ui/core/Grid';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import AddIcon from '@material-ui/icons/Add';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import compose from 'helpers/compose';
import { formatAmountValue, sqlDateToLabel, fractionToPercent } from 'helpers/helpers';
import Info from 'components/Tooltip/Info';
import LockIcon from 'components/Icons/LockIcon';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import Tooltip from 'components/Tooltip/Tooltip';

import { WorkflowPlanStore } from 'stores/workflowStores/newWorkflowPlanStore/workflowPlanStore';
import { ReservesStore } from 'stores/workflowStores/finances/newReservesStore/reservesStore';
import { Reserve, ReservesData } from 'stores/workflowStores/finances/newReservesStore/model';
import { RootStore } from 'stores/rootStore';
import useStyles from './styles/moduleStyles';
import { ViewMode } from './viewModes';
import ReserveModal from './ReservesModal';
import { EdwardColors, Currency } from 'enums';

interface Props {
  accountId: string;
  currency: Currency;
  rootStore: RootStore;
  reservesData?: ReservesData;
  plan?: boolean;
  refresh?: () => void;
  readOnly?: boolean;
  userMode?: boolean;
  viewMode?: ViewMode;
  moduleInfo?: string;
  collapsed?: boolean;
  planStore?: WorkflowPlanStore;
  openRent?: (open: boolean) => void;
  rentSet?: boolean;
  overview?: boolean;
  original?: boolean;
}

const Reserves: React.FC<Props> = ({
  accountId,
  reservesData,
  currency,
  rootStore,
  plan,
  refresh,
  readOnly,
  viewMode,
  moduleInfo,
  collapsed,
  planStore,
  userMode,
  openRent,
  rentSet,
  overview,
  original,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [isReserveOpen, setIsReserveOpen] = useState(false);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [reserveId, setReserveId] = useState<string | undefined>(undefined);
  const [reservesStore] = useState(new ReservesStore(rootStore, accountId, viewMode));
  const [showDetail, setShowDetail] = useState(!collapsed);
  const [allReservesCount, setAllReservesCount] = useState(reservesData?.reserves.length || 0);
  const [originalReserve, setOriginalReserve] = useState<Reserve | undefined>(undefined);

  useEffect(() => {
    if (!reservesData) {
      reservesStore.fetchAllReserves();
    }
    setShowDetail(!collapsed);
  }, [accountId, reservesData, reservesStore, collapsed]);

  useEffect(() => {
    setAllReservesCount(reservesData?.reserves.length || 0);
  }, [reservesData]);

  reservesData = reservesData ? reservesData : reservesStore.allReserves || undefined;

  const handleReserveClose = (showChangeDialog?: boolean) => {
    setIsReserveOpen(false);
    setOriginalReserve(undefined);
    planStore && planStore.setEditReserve(null);
    viewMode === ViewMode.DETAIL && showChangeDialog && setChangeDialogOpen(true);
  };

  const editReserveId = planStore?.editReserve;

  useEffect(() => {
    editReserveId && editReserve(editReserveId);
  }, [editReserveId]);

  const editReserve = (targetId: string) => {
    setReserveId(targetId);
    setIsReserveOpen(true);
  };

  const copyReserve = (reserve: Reserve) => {
    setOriginalReserve(reserve);
    setIsReserveOpen(true);
  };

  const handleRefresh = async () => {
    if (refresh) {
      await refresh();
    } else {
      await reservesStore.fetchAllReserves();
    }
    setOriginalReserve(undefined);
  };

  const [confirmDialog, setConfirmDialog] = useState<string | undefined>(undefined);

  const listReserves = (reserves: Reserve[], fullWidth: boolean) => {
    return (
      <>
        {reserves &&
          reserves.map((reserve) => {
            return (
              <Grid
                container
                item
                xs={12}
                sm={12}
                md={12}
                lg={!plan || fullWidth ? 12 : 6}
                spacing={1}
                key={reserve.id}
                alignItems="stretch"
              >
                <Grid
                  container
                  item
                  alignContent="center"
                  alignItems="center"
                  className={`${classes.item} ${overview && original ? classes.inactive : classes.reserve}`}
                >
                  <Grid item xs={2} sm={2} xl={1} className={classes.icon}>
                    <LockIcon
                      fontSize="inherit"
                      htmlColor={overview && original ? EdwardColors.GRAY_MEDIUM : undefined}
                    />
                  </Grid>
                  <Grid
                    container
                    item
                    xs={userMode || overview ? 10 : 7}
                    sm={userMode || overview ? 10 : 7}
                    xl={userMode || overview ? 11 : 8}
                    spacing={1}
                    className={classes.info}
                  >
                    <Grid item>
                      <Typography variant="h3" className={`${classes.title}`} title={reserve.title}>
                        {reserve.title}
                      </Typography>
                      <span>
                        <strong>{formatAmountValue(reserve.value.value, currency)}</strong>
                      </span>
                      <span>
                        {t('general_portfolio')}
                        :&nbsp;
                        <strong>{t(`finQuestionnary_reserve_bucket_${reserve.bucket}`)}</strong>
                      </span>
                      <span>
                        {!reserve?.until && `${t(`options_horizon_${reserve.horizon}`)}`}
                        {reserve?.until && (
                          <>
                            {` ${t('general_until')}: `}
                            {` ${sqlDateToLabel(reserve.until)}`}
                          </>
                        )}
                      </span>
                      {reserve.cancellationLimitActive && (
                        <span>
                          {reserve.cancellationLimitActive &&
                            `${t('reserve_cancel')} ${fractionToPercent(
                              reserve.cancellationLimit as number,
                              0,
                              true,
                            )}% ${t('reserve_cancelFall')}`}
                          {reserve.currentLimit !== null && reserve.currentLimit !== undefined && (
                            <>
                              ,{' '}
                              <strong>
                                &nbsp;
                                {`${t('general_currently')} ${fractionToPercent(
                                  reserve.currentLimit as number,
                                  1,
                                  true,
                                )}%`}
                              </strong>
                            </>
                          )}
                          {reserve.cancellationLimitActive && (
                            <Info inline content={t('tooltip_reserves_cancellationValue')} />
                          )}
                        </span>
                      )}

                      {reserve.newDeposits && <span>{t('reserve_newDeposits').toLowerCase()}</span>}
                    </Grid>
                  </Grid>
                  {!userMode && !overview && (
                    <Grid item xs={3} className={classes.actions}>
                      <Tooltip title={readOnly ? t('accountDetail_readOnlyInfo') : ''}>
                        <div>
                          <IconButton
                            size="medium"
                            className="accountEdit-button"
                            onClick={() => copyReserve({ ...reserve, id: undefined, title: '' })}
                            disabled={readOnly}
                          >
                            <FileCopyOutlinedIcon fontSize="inherit" />
                          </IconButton>

                          <IconButton
                            size="medium"
                            className="accountEdit-button"
                            onClick={() => editReserve(reserve.id!)}
                            disabled={readOnly}
                          >
                            <EditIcon fontSize="inherit" />
                          </IconButton>

                          <IconButton
                            size="medium"
                            className="account-delete-button"
                            onClick={() => setConfirmDialog(reserve.id)}
                            disabled={readOnly}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </div>
                      </Tooltip>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            );
          })}
      </>
    );
  };

  return reservesData ? (
    <>
      <ReserveModal
        currency={currency}
        opened={isReserveOpen}
        handleClose={handleReserveClose}
        reservesStore={reservesStore}
        options={reservesData.options}
        reserveId={reserveId}
        refresh={handleRefresh}
        openRent={openRent}
        originalReserve={originalReserve}
      />

      <ConfirmDialog
        noHeader
        isOpen={confirmDialog !== undefined}
        confirm={async () => {
          await reservesStore?.deleteReserve(confirmDialog!);
          setConfirmDialog(undefined);
          await handleRefresh();
          viewMode === ViewMode.DETAIL && setChangeDialogOpen(true);
        }}
        close={() => {
          setConfirmDialog(undefined);
        }}
      >
        {t('reserves_confirmDelete')}
      </ConfirmDialog>

      <ConfirmDialog noHeader isOpen={changeDialogOpen} confirm={() => setChangeDialogOpen(false)}>
        <div
          dangerouslySetInnerHTML={{
            __html: t(`reserves_change_text`, {
              interpolation: { escapeValue: false },
            }),
          }}
        />
      </ConfirmDialog>

      {!overview && (
        <Grid container spacing={0}>
          <Grid item>
            <Typography variant="h3" className={classes.moduleTitle}>
              {collapsed !== undefined ? (
                <Link
                  className={classes.showLink}
                  id="toggle-targets-link"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setShowDetail(!showDetail);
                  }}
                >
                  {t('accountPlan_reserves')}
                  <span className={classes.count}> ({allReservesCount})</span>
                  <Info content={t('tooltip_reserves')} heading />
                  <ArrowDropDownIcon
                    htmlColor={EdwardColors.SECONDARY}
                    className={`${classes.showIcon} ${showDetail ? classes.shownIcon : ''}`}
                  />
                </Link>
              ) : (
                <>
                  {t('accountPlan_reserves')}
                  <Info content={t('tooltip_reserves')} heading />
                </>
              )}
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: plan ? undefined : '16px' }}>
            {!readOnly && reservesData.addingEnabled && !userMode && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setReserveId(undefined);
                  setIsReserveOpen(true);
                }}
                startIcon={<AddIcon fontSize="medium" />}
                className="secondaryGold"
              >
                {t('add_reserve')}
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {moduleInfo && <Typography>{moduleInfo}</Typography>}
      <Collapse in={showDetail} className={classes.collapse}>
        <Grid container spacing={0} alignItems="stretch" className={classes.moduleWrapper}>
          {listReserves(reservesData.reserves, !plan)}
        </Grid>
      </Collapse>
    </>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Reserves);
